import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DollarSign, Plus, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BudgetPage = () => {
  const [budgetItems, setBudgetItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    category: '',
    item_name: '',
    estimated_cost: '',
    actual_cost: '',
    paid: false
  });

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/budget`, { withCredentials: true });
      setBudgetItems(data);
    } catch (error) {
      toast.error('Error fetching budget');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/budget`, {
        ...form,
        estimated_cost: parseFloat(form.estimated_cost),
        actual_cost: form.actual_cost ? parseFloat(form.actual_cost) : null
      }, { withCredentials: true });
      toast.success('Budget item added!');
      setDialogOpen(false);
      setForm({ category: '', item_name: '', estimated_cost: '', actual_cost: '', paid: false });
      fetchBudget();
    } catch (error) {
      toast.error('Error adding budget item');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalEstimated = budgetItems.reduce((sum, item) => sum + item.estimated_cost, 0);
  const totalActual = budgetItems.reduce((sum, item) => sum + (item.actual_cost || 0), 0);
  const difference = totalEstimated - totalActual;

  const categories = [...new Set(budgetItems.map(item => item.category))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Budget</h1>
          <p className="text-muted-foreground mt-1">Track your wedding expenses</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-budget-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Budget Item</DialogTitle>
              <DialogDescription>Add a new expense to your wedding budget</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  placeholder="e.g., Venue, Catering, Photography"
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  required
                  data-testid="budget-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Item Name</Label>
                <Input
                  placeholder="e.g., Wedding hall booking"
                  value={form.item_name}
                  onChange={(e) => setForm({...form, item_name: e.target.value})}
                  required
                  data-testid="budget-item-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Cost (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.estimated_cost}
                    onChange={(e) => setForm({...form, estimated_cost: e.target.value})}
                    required
                    data-testid="budget-estimated-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Actual Cost (₹, optional)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.actual_cost}
                    onChange={(e) => setForm({...form, actual_cost: e.target.value})}
                    data-testid="budget-actual-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-budget-button">
                Add Budget Item
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Estimated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-estimated">{formatINR(totalEstimated)}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Spent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-spent">{formatINR(totalActual)}</div>
          </CardContent>
        </Card>
        <Card className={`shadow-sm ${difference >= 0 ? 'bg-green-50 dark:bg-green-950' : 'bg-red-50 dark:bg-red-950'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Difference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className={`text-2xl font-bold ${difference >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="budget-difference">
                {formatINR(Math.abs(difference))}
              </div>
              {difference >= 0 ? (
                <TrendingDown className="h-5 w-5 text-green-600" />
              ) : (
                <TrendingUp className="h-5 w-5 text-red-600" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {difference >= 0 ? 'Under budget' : 'Over budget'}
            </p>
          </CardContent>
        </Card>
      </div>

      {budgetItems.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No budget items yet. Add your first expense!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {categories.map((category) => {
            const categoryItems = budgetItems.filter(item => item.category === category);
            const categoryTotal = categoryItems.reduce((sum, item) => sum + item.estimated_cost, 0);
            const categorySpent = categoryItems.reduce((sum, item) => sum + (item.actual_cost || 0), 0);
            
            return (
              <div key={category}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-medium">{category}</h2>
                  <div className="text-sm text-muted-foreground">
                    {formatINR(categorySpent, { compact: true })} / {formatINR(categoryTotal, { compact: true })}
                  </div>
                </div>
                <div className="space-y-2">
                  {categoryItems.map((item) => (
                    <Card key={item.id} className="shadow-sm" data-testid="budget-item-card">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium">{item.item_name}</h3>
                            <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                              <span>Estimated: {formatINR(item.estimated_cost)}</span>
                              {item.actual_cost && (
                                <span>Actual: {formatINR(item.actual_cost)}</span>
                              )}
                            </div>
                          </div>
                          {item.paid && (
                            <div className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded-full">
                              Paid
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};